import styled from '@emotion/styled';
import React, { Key, useCallback, useEffect, useMemo } from 'react';
import { buildCSSWithLength } from '../../styles';
import { useProviderConfig } from '../provider';
import { TableCell } from './TableCell';
import { TableRow } from './TableRow';
import {
  DataTableProps,
  DataTableRenderExpandedRow,
  SelectRowAction,
  DataTableRowType,
  ExpandedRowAction,
  DataTableRowSelectionRecord,
} from './types';
import { getFlattenColumns } from './utils';
import { State } from '../../container';
import { DataTableRow } from './DataTableRow';
import { VirtualItem } from '@tanstack/react-virtual';

interface DataTableRowsProps<DataType>
  extends Pick<
    DataTableProps<DataType>,
    | 'columns'
    | 'rows'
    | 'noDataText'
    | 'noDataPadding'
    | 'loading'
    | 'selectableRows'
    | 'clickableRows'
    | 'cellHoverStyle'
    | 'cellDisabledStyle'
    | 'resize'
  > {
  tableContainerAttribute: number;
  totalColumnCount: number;
  allSelected?: boolean;
  selectedRows?: Map<Key, DataTableRowSelectionRecord<DataType>>;
  expandedRows?: Key[];
  expandableButton: boolean;
  renderExpandedRow?: DataTableRenderExpandedRow<DataType>;
  dividerRows?: (row: DataType, index: number) => boolean;
  rowColor?: (row: DataTableRowType<DataType>, index: number) => (string | undefined) | string;
  rowKeyExtractor: (row: DataType, index: number) => Key;
  rowSelectionKeyExtractor: (row: DataType, index: number) => Key;
  onSelectedRow?: (action: SelectRowAction<DataType>) => void;
  onExpandedRow?: (action: ExpandedRowAction<DataType>) => void;
  virtualizedItems?: VirtualItem[];
}

export const DataTableRows = <DataType extends object>({
  columns,
  rows,
  loading,
  selectableRows,
  clickableRows,
  selectedRows,
  expandedRows,
  noDataText,
  noDataPadding,
  cellHoverStyle,
  cellDisabledStyle,
  tableContainerAttribute,
  totalColumnCount,
  resize,
  expandableButton,
  renderExpandedRow,
  dividerRows,
  onSelectedRow,
  onExpandedRow,
  rowColor,
  rowKeyExtractor,
  rowSelectionKeyExtractor,
  virtualizedItems,
}: DataTableRowsProps<DataType>) => {
  const { locale } = useProviderConfig();
  const computedFlattenColumns = useMemo(() => getFlattenColumns({ columns }), [columns]);

  const handleSelectRow = useCallback(
    (row: DataTableRowType<DataType>, index: number, selected: boolean) => {
      onSelectedRow?.({ type: 'SELECT_ROW', rows, row, index, selected });
    },
    [onSelectedRow, rows],
  );

  const handleExpandRow = useCallback(
    (row: DataTableRowType<DataType>, index: number, expanded: boolean) => {
      onExpandedRow?.({ type: 'EXPAND_ROW', row, index, expanded });
    },
    [onExpandedRow],
  );

  const EmptyRow = () => (
    <TableRow isEmpty>
      <TableCell className='pds-table-cell pds-table-cell-empty' colSpan={totalColumnCount} isEmpty isRowLast>
        <EmptyContent
          maxWidth={tableContainerAttribute > 0 ? tableContainerAttribute : undefined}
          paddingBlock={noDataPadding ?? 120}
        >
          <State
            kind={loading ? 'loading' : 'empty'}
            description={loading ? '' : noDataText || locale?.Table.noDataText}
          />
        </EmptyContent>
      </TableCell>
    </TableRow>
  );

  const items = useMemo(
    () =>
      virtualizedItems
        ? virtualizedItems.map((virtualItem) => {
            return {
              row: rows[virtualItem.index],
              rowIndex: virtualItem.index,
            };
          })
        : rows.map((row, index) => {
            return {
              row,
              rowIndex: index,
            };
          }),
    [virtualizedItems, rows],
  );

  useEffect(() => {
    const isUnique =
      new Set(
        items.map((item, index) => {
          const rowType = item.row as DataTableRowType<DataType>;
          return rowKeyExtractor(rowType, index);
        }),
      ).size === items.length;

    if (!isUnique) {
      throw new Error('rowKeyExtractor 고유한 값을 반환해야합니다.');
    }
  }, [items, rowKeyExtractor]);

  if (loading || rows.length === 0) {
    return <EmptyRow />;
  }

  return (
    <>
      {items.map(({ row, rowIndex }) => {
        const rowType = row as DataTableRowType<DataType>;
        const selectionKey = (selectableRows || renderExpandedRow) && rowSelectionKeyExtractor(rowType, rowIndex);
        const extractedKey = rowKeyExtractor(rowType, rowIndex);
        const selected = selectionKey && selectedRows?.has(selectionKey);
        const expanded = selectionKey && expandedRows?.includes(selectionKey);

        return (
          <DataTableRow
            key={extractedKey}
            rows={rows}
            columns={computedFlattenColumns}
            index={rowIndex}
            row={rowType}
            isRowLast={rowIndex === rows.length - 1}
            selected={Boolean(selected)}
            expanded={Boolean(expanded)}
            rowColor={typeof rowColor === 'string' ? rowColor : rowColor?.(rowType, rowIndex)}
            selectionKey={selectionKey}
            onExpandedRow={handleExpandRow}
            onSelectedRow={handleSelectRow}
            expandableButton={expandableButton}
            renderExpandedRow={renderExpandedRow}
            dividerRows={dividerRows}
            selectableRows={selectableRows}
            clickableRows={clickableRows}
            cellHoverStyle={cellHoverStyle}
            cellDisabledStyle={cellDisabledStyle}
            resize={resize}
          />
        );
      })}
    </>
  );
};

const EmptyContent = styled.div<{ maxWidth?: number; paddingBlock: number }>`
  position: sticky;
  left: 0;
  padding: ${({ paddingBlock }) => `${paddingBlock}px`} 0;
  width: 100%;
  text-align: center;
  ${({ maxWidth }) => typeof maxWidth === 'number' && buildCSSWithLength('max-width', maxWidth)};
`;
